/**
 * Allows user to set up Figma integration for a space.
 */

import React, { useState, useCallback } from "react";
import { Text, ActivityIndicator, Flex, Icon, Notice } from "@thenounproject/lingo-core";
import ModalBody from "../../../../../app/_components/ModalBody";
import ModalHeader from "../../../../../app/_components/ModalHeader";
import useFigmaIntegrationStatus from "@redux/actions/figma/useFigmaIntegrationStatus";
import { useSelectSpace } from "@selectors/entities/spaces";
import useInitiateFigmaOAuth from "@actions/figma/useInitiateFigmaOAuth";
import ModalFooter from "../../../../../app/_components/ModalFooter";

const FigmaIntegrationSetupModal: React.FC = () => {
  const space = useSelectSpace();
  const spaceId = space.id;
  const [initiateFigmaOAuth] = useInitiateFigmaOAuth();

  const [isProcessing, setProcessing] = useState(false),
    [keyError, setKeyError] = useState(null);

  const { data: figma } = useFigmaIntegrationStatus({ spaceId }),
    integrationActive = figma?.auth?.status === "active";

  const startOAuthFlow = useCallback(async () => {
    setProcessing(true);
    setKeyError(null);
    const { response, error: responseError } = await initiateFigmaOAuth({ spaceId });
    if (responseError) {
      setProcessing(false);
      setKeyError(responseError.message);
    } else if (response?.result?.authorizationUrl) {
      window.location.href = response.result.authorizationUrl;
    }
  }, [initiateFigmaOAuth, spaceId]);

  function renderContent() {
    const { auth } = figma;

    if (integrationActive) {
      if (auth.authType === "oauth") {
        return (
          <Flex>
            <Icon mr="xs" iconId="checkmark" fill="success" />
            <Text>Connected via OAuth as {auth.figmaHandle}.</Text>
          </Flex>
        );
      }
      return (
        <Flex>
          <Icon mr="xs" iconId="checkmark" fill="success" />
          <Text>Connected as {auth.figmaHandle}.</Text>
        </Flex>
      );
    } else {
      return (
        <>
          {auth?.status === "error" && (
            <Notice
              noticeStyle="error"
              message={
                auth.message || "There was a problem with your connection, please reconnect."
              }
            />
          )}
          <Flex flexDirection="column">
            <Text font="ui.regular" color="black" mb="s">
              Sign in with your Figma account to securely connect your workspace.
            </Text>
            {keyError && <Notice message={keyError} noticeStyle="error" mt="s" />}
          </Flex>
        </>
      );
    }
  }

  return (
    <>
      <ModalHeader title="Figma Integration" />
      <ModalBody>
        <Text textAlign="left" mb="l">
          Our Figma integration helps you add your Figma assets to Lingo easily.
        </Text>
        {!figma ? <ActivityIndicator center /> : renderContent()}
      </ModalBody>
      <ModalFooter
        primary={{
          disabled: isProcessing,
          text: "Connect with Figma",
          onClick: startOAuthFlow,
        }}
      />
    </>
  );
};

export default FigmaIntegrationSetupModal;
